import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  remoteAttendanceApi,
  REASON_OPTIONS,
  DATE_PRESET_OPTIONS,
  STATUS_OPTIONS,
} from '../api/remoteAttendanceApi';
import { appToast } from '../../../../components/AppToast';
import { appConfirm } from '../../../../components/AppConfirmModal';
import { executePrintReport } from '../../../../utils/printReportUtil';
import { useExportJob } from '../../../../hooks/useExportJob';

const REPORT_COLUMNS = [
  { key: 'employee', label: 'Employee Name' },
  { key: 'employeeId', label: 'Employee Code' },
  { key: 'dateTimeDisplay', label: 'Punch Date & Time' },
  { key: 'reason', label: 'Reason' },
  { key: 'remarks', label: 'Remarks' },
  { key: 'gpsLocation', label: 'GPS Location' },
  { key: 'status', label: 'Status' },
];

export function useRemoteAttendanceList() {
  const navigate = useNavigate();
  const [records, setRecords] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Background Export integration
  const { triggerExport, isExporting } = useExportJob({
    module: 'remote_attendance',
    jobType: 'EXPORT',
  });

  // Status Tab (Pending | Approved | Rejected | All)
  const [statusFilter, setStatusFilter] = useState('Pending');

  // Filters
  const [employeeFilter, setEmployeeFilter] = useState('');
  const [reasonFilter, setReasonFilter] = useState('All');
  const [datePresetFilter, setDatePresetFilter] = useState('All');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  // Search
  const [searchField, setSearchField] = useState('Name');
  const [searchText, setSearchText] = useState('');

  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [reasonOptions, setReasonOptions] = useState(REASON_OPTIONS);

  // Load dynamic reasons from ManualPunchReason table
  useEffect(() => {
    remoteAttendanceApi
      .getReasons()
      .then((reasons) => {
        if (Array.isArray(reasons) && reasons.length > 0) {
          const mapped = [
            { value: 'All', label: 'All Reasons' },
            ...reasons.map((r) => ({
              value: String(r.ID || r.id),
              label: r.Name || r.name,
              id: r.ID || r.id,
            })),
          ];
          setReasonOptions(mapped);
        }
      })
      .catch(() => {});
  }, []);

  const fetchRecords = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await remoteAttendanceApi.getRecords({
        status: statusFilter,
        searchText: searchText || undefined,
        employeeId: employeeFilter && employeeFilter !== 'All' ? Number(employeeFilter) : undefined,
        reasonId: reasonFilter && reasonFilter !== 'All' ? Number(reasonFilter) : undefined,
        fromDate: fromDate || undefined,
        toDate: toDate || undefined,
        page,
        pageSize,
      });
      setRecords(data.items || []);
      setTotalCount(data.total || data.total_count || 0);
      setTotalPages(data.totalPages || data.total_pages || 1);
    } catch (err) {
      console.error(err);
      appToast.error('Failed to fetch remote attendance records.');
      setRecords([]);
    } finally {
      setIsLoading(false);
    }
  }, [
    statusFilter,
    employeeFilter,
    searchText,
    reasonFilter,
    fromDate,
    toDate,
    page,
    pageSize,
  ]);

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  const handleStatusTabChange = (status) => {
    setStatusFilter(status);
    setPage(1);
  };

  const handleDatePresetChange = (preset) => {
    setDatePresetFilter(preset);
    const now = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    const toYMD = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

    if (preset === 'Today') {
      const dStr = toYMD(now);
      setFromDate(dStr);
      setToDate(dStr);
    } else if (preset === 'Yesterday') {
      const y = new Date(now);
      y.setDate(y.getDate() - 1);
      const dStr = toYMD(y);
      setFromDate(dStr);
      setToDate(dStr);
    } else if (preset === 'This Week') {
      const day = now.getDay();
      const diffToMon = now.getDate() - (day === 0 ? 6 : day - 1);
      const mon = new Date(now.getFullYear(), now.getMonth(), diffToMon);
      const sun = new Date(mon);
      sun.setDate(mon.getDate() + 6);
      setFromDate(toYMD(mon));
      setToDate(toYMD(sun));
    } else if (preset === 'This Month') {
      const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
      const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      setFromDate(toYMD(firstDay));
      setToDate(toYMD(lastDay));
    } else if (preset === 'Last Month') {
      const firstDay = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const lastDay = new Date(now.getFullYear(), now.getMonth(), 0);
      setFromDate(toYMD(firstDay));
      setToDate(toYMD(lastDay));
    } else if (preset === 'This Quarter') {
      const quarterIndex = Math.floor(now.getMonth() / 3);
      const from = new Date(now.getFullYear(), quarterIndex * 3, 1);
      const to = new Date(now.getFullYear(), (quarterIndex + 1) * 3, 0);
      setFromDate(toYMD(from));
      setToDate(toYMD(to));
    } else if (preset === 'This Financial Year') {
      const curMonth = now.getMonth();
      const curYear = now.getFullYear();
      const startYear = curMonth >= 3 ? curYear : curYear - 1;
      const from = new Date(startYear, 3, 1);
      const to = new Date(startYear + 1, 2, 31);
      setFromDate(toYMD(from));
      setToDate(toYMD(to));
    } else if (preset === 'All') {
      setFromDate('');
      setToDate('');
    }
    setPage(1);
  };

  const handleFromDateChange = (val) => {
    setFromDate(val);
    setDatePresetFilter('Custom');
    setPage(1);
  };

  const handleToDateChange = (val) => {
    setToDate(val);
    setDatePresetFilter('Custom');
    setPage(1);
  };

  const handleSearch = () => {
    setPage(1);
    fetchRecords();
  };

  const resetFilters = () => {
    setEmployeeFilter('');
    setReasonFilter('All');
    setDatePresetFilter('All');
    setFromDate('');
    setToDate('');
    setStatusFilter('Pending');
    setSearchField('Name');
    setSearchText('');
    setPage(1);
  };

  const handleApprove = async (id, name) => {
    const confirmed = await appConfirm({
      title: 'Approve Remote Attendance',
      message: `Are you sure you want to approve remote attendance for "${name}"?`,
      confirmText: 'Approve',
      confirmVariant: 'primary',
    });
    if (!confirmed) return;

    try {
      await remoteAttendanceApi.updateStatus(id, 'Approved');
      appToast.success(`Remote attendance for "${name}" approved successfully.`);
      fetchRecords();
    } catch (err) {
      console.error(err);
      appToast.error('Failed to approve attendance.');
    }
  };

  const handleReject = async (id, name) => {
    const confirmed = await appConfirm({
      title: 'Reject Remote Attendance',
      message: `Are you sure you want to reject remote attendance for "${name}"?`,
      confirmText: 'Reject',
      confirmVariant: 'danger',
    });
    if (!confirmed) return;

    try {
      await remoteAttendanceApi.updateStatus(id, 'Rejected');
      appToast.warning(`Remote attendance for "${name}" marked as Rejected.`);
      fetchRecords();
    } catch (err) {
      console.error(err);
      appToast.error('Failed to reject attendance.');
    }
  };

  const handleDelete = async (id, name) => {
    const confirmed = await appConfirm({
      title: 'Delete Remote Attendance',
      message: `Are you sure you want to delete remote attendance for "${name}"? This action cannot be undone.`,
      confirmText: 'Delete',
      confirmVariant: 'danger',
    });
    if (!confirmed) return;

    try {
      await remoteAttendanceApi.deleteRecord(id);
      appToast.success(`Record for "${name}" deleted.`);
      fetchRecords();
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Failed to delete record.';
      appToast.error(msg);
    }
  };

  const handleToolSelect = (toolKey) => {
    const activeFilters = {
      status: statusFilter !== 'All' ? statusFilter : undefined,
      searchText: searchText || undefined,
      employeeId: employeeFilter && employeeFilter !== 'All' ? Number(employeeFilter) : undefined,
      reasonId: reasonFilter && reasonFilter !== 'All' ? Number(reasonFilter) : undefined,
      fromDate: fromDate || undefined,
      toDate: toDate || undefined,
    };

    if (toolKey === 'upload') {
      navigate('/payroll/remote-attandance/upload');
    } else if (toolKey === 'print') {
      executePrintReport({
        title: 'Remote Attendance Report',
        columns: REPORT_COLUMNS,
        metadata: {
          'Status': statusFilter || 'All',
          'Date Range': fromDate && toDate ? `${fromDate} to ${toDate}` : (datePresetFilter || 'All'),
          'Search': searchText || 'All',
        },
        fetchReportData: async () => {
          const res = await remoteAttendanceApi.getRecords({
            ...activeFilters,
            page: 1,
            pageSize: 1000,
          });
          return { items: res?.items || [] };
        },
        onExportPdf: () => triggerExport(activeFilters, 'PDF'),
        onExportExcel: () => triggerExport(activeFilters, 'XLSX'),
        threshold: 1000,
      });
    } else if (toolKey === 'export_excel') {
      triggerExport(activeFilters, 'XLSX');
    } else if (toolKey === 'export_pdf') {
      triggerExport(activeFilters, 'PDF');
    }
  };

  const tools = [
    { key: 'print', label: 'Print Report', icon: 'print' },
    { key: 'export_excel', label: 'Export Excel', icon: 'description' },
    { key: 'export_pdf', label: 'Export PDF', icon: 'picture_as_pdf' },
    { key: 'upload', label: 'Upload', icon: 'cloud_upload' },
  ];

  const searchFieldOptions = [
    { value: 'Name', label: 'Employee Name' },
  ];

  return {
    state: {
      records,
      isLoading,
      isExporting,
      statusFilter,
      employeeFilter,
      reasonFilter,
      datePresetFilter,
      fromDate,
      toDate,
      searchField,
      searchText,
      page,
      pageSize,
      totalCount,
      totalPages,
      reasonOptions,
      datePresetOptions: DATE_PRESET_OPTIONS,
      searchFieldOptions,
      statusOptions: STATUS_OPTIONS,
      tools,
    },
    actions: {
      setStatusFilter: handleStatusTabChange,
      handleStatusTabChange,
      setEmployeeFilter,
      setReasonFilter,
      setDatePresetFilter: handleDatePresetChange,
      setDatePreset: handleDatePresetChange,
      setFromDate: handleFromDateChange,
      setToDate: handleToDateChange,
      setSearchField,
      setSearchText,
      setPage,
      setPageSize,
      handleSearch,
      resetFilters,
      handleApprove,
      handleReject,
      handleDelete,
      handleToolSelect,
      fetchRecords,
      refresh: fetchRecords,
    },
  };
}

